"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.OrcadDsnTransformGlobalSymbolGenerator = void 0;
const sigma_engine_shared_1 = require("@cadstrom/sigma-engine-shared");
const TransformSchematic_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformSchematic");
const Transform_1 = require("@cadstrom/sigma-engine-shared/dist/Utils/Transform");
const OrcadDsnUtils_1 = require("./OrcadDsnUtils");
const TransformModule_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformModule");
const TransformMultiInstanceDisplayModule_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformMultiInstanceDisplayModule");
const TransformMultiInstanceSchematic_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformMultiInstanceSchematic");
const TransformModulePinConnection_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformModulePinConnection");
const TransformProcessingResult_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProcessingResult");
const TransformComponent_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformComponent");
const TransformComponentSymbol_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformComponentSymbol");
const TransformImageExtraction_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformImageExtraction");
const TransformProjectModule_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProjectModule");
const TransformProject_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformProject");
const TransformObject_1 = require("../../../../Shared/HierarchicalTransformer/TransformObjects/TransformObject");
const orcadLayers_1 = require("../../../orcadLayers");
const orcadShapeConverters_1 = require("../../../orcadShapeConverters");
const OrcadDsnSymbolUtils_1 = require("./OrcadDsnSymbolUtils");
const orcadLibraryProvenance_1 = require("../../../orcadLibraryProvenance");
const ModelText_1 = require("../../../../../ModelText");
const CADENCE_GROUND_SYMBOL_NAMES = new Map([
    ['CAPSYM', new Set(['GND', 'GND_CHASSIS', 'GND_EARTH', 'GND_FIELD_SIGNAL', 'GND_POWER', 'GND_SIGNAL'])],
    ['SOURCE', new Set(['0'])],
]);
const PROJECT_GROUND_SYMBOL_PATTERN = /^(?:.*[^A-Za-z0-9])?[ADPSECF]?(?:GND|GROUND|EARTH)(?:_\d+)?$/i;
function isGroundSymbol(globalSymbolDef) {
    for (const [libraryName, symbolNames] of CADENCE_GROUND_SYMBOL_NAMES) {
        if ((0, orcadLibraryProvenance_1.isFromCadenceLibrary)(globalSymbolDef.SourceLibName, libraryName)) {
            return symbolNames.has(globalSymbolDef.Name.toUpperCase());
        }
    }
    return PROJECT_GROUND_SYMBOL_PATTERN.test(globalSymbolDef.Name);
}
function symbolPinRef(pin) {
    return Number.isFinite(pin.Position) ? pin.Position.toString() : pin.Name;
}
class OrcadDsnTransformGlobalSymbolGenerator {
    constructor() {
        this.runsFor = [TransformSchematic_1.TransformSchematic];
        this.globalSymbolNum = 1;
    }
    *generate(parent, _rootContext) {
        const page = parent.hierarchyEntry.page;
        const instance = parent.instance;
        for (const globalSymbol of page.Global ?? []) {
            yield* this.generateFromGlobalSymbol(parent, globalSymbol, instance);
        }
    }
    *generateFromGlobalSymbol(parent, globalSymbol, instance) {
        const design = parent.hierarchyEntry.design;
        const globalSymbolResolution = this.findGlobalSymbol(design.Cache?.GlobalSymbol, globalSymbol);
        const globalSymbolDef = globalSymbolResolution.symbol;
        if (globalSymbolResolution.ambiguous) {
            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                type: 'symbol-variant-ambiguous',
                message: `[symbol-variant-ambiguous] global symbol="${globalSymbol.SymbolName}" library="${globalSymbol.LibName ?? ''}" does not select exactly one sourced cache entry; withheld`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
            return;
        }
        if (!globalSymbolDef) {
            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                type: 'orcad-dsn-global-symbol-missing',
                message: `Could not find global symbol definition for "${globalSymbol.SymbolName}"`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
            return;
        }
        const component = new TransformComponent_1.TransformComponent(parent, {
            baseComponent: {
                name: globalSymbol.SymbolName,
                isVirtual: true,
                metadata: {
                    'orcad.globalSymbol.name': globalSymbol.Name,
                    'orcad.globalSymbol.sourceLibName': globalSymbolDef.SourceLibName,
                },
            },
        });
        yield component;
        const bboxCenter = [
            (globalSymbolDef.BoundingBox.X1 + globalSymbolDef.BoundingBox.X2) / 2,
            (globalSymbolDef.BoundingBox.Y1 + globalSymbolDef.BoundingBox.Y2) / 2,
        ];
        const pointTransform = (p) => (0, Transform_1.translatePoint)(p, [-bboxCenter[0], -bboxCenter[1]]);
        const pins = (globalSymbolDef.PinScalar ?? []).map((p) => ({
            type: 'pin',
            ref: symbolPinRef(p),
            name: p.Name,
            number: symbolPinRef(p),
            start: pointTransform([p.StartX, p.StartY]),
            end: pointTransform([p.HotptX, p.HotptY]),
            layer: orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Pin].id,
            width: 1,
            drawings: [],
            metadata: { 'orcad.pin.position': p.Position },
        }));
        const drawings = orcadShapeConverters_1.OrCadShapeConvert.elementWithGeometries({
            pointTransform,
        })(globalSymbolDef).map((d) => ({
            layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Symbol].id],
            drawing: d,
        }));
        const bitmapResult = orcadShapeConverters_1.OrCadShapeConvert.elementBitmaps({ pointTransform })(globalSymbolDef);
        drawings.push(...bitmapResult.drawings.map((d) => ({
            layers: [orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.Image].id],
            drawing: d,
        })));
        for (const ie of bitmapResult.imageExtractions) {
            yield new TransformImageExtraction_1.TransformImageExtraction(parent, ie.ref, ie.name, ie.data, ie.mimeType);
        }
        const symbol = new TransformComponentSymbol_1.TransformComponentSymbol(component, {
            ref: globalSymbol.SymbolName,
            name: globalSymbol.SymbolName,
            pins,
            drawings,
            metadata: { 'orcad.symbol.boundingbox': globalSymbolDef.BoundingBox },
        });
        const connectionPin = pins[0];
        if (connectionPin) {
            symbol.setPinSignal(connectionPin, {
                id: connectionPin.name,
            });
        }
        yield symbol;
        const center = OrcadDsnUtils_1.OrcadDsnUtils.computeModuleCenter(globalSymbol, globalSymbolDef.BoundingBox);
        const moduleRef = `${instance.pathUniqueId}::${globalSymbol.DbId}`;
        const isMultiInstance = instance.totalSheetInstances > 1;
        const moduleProps = {
            ref: moduleRef,
            footprintRef: globalSymbol.SymbolName,
            center,
            transform: {
                pos: [0, 0],
                angle: globalSymbol.Rotation * 90,
                mirrored: globalSymbol.Mirror === 1 ?
                    [
                        globalSymbol.Rotation === 1 || globalSymbol.Rotation === 3,
                        globalSymbol.Rotation === 2 || globalSymbol.Rotation === 0,
                    ]
                    : [false, false],
            },
            name: `#PWR${(this.globalSymbolNum++).toString().padStart(3, '0')}`,
            drawProperties: {},
            metadata: {
                Value: globalSymbol.Name,
                'orcad.globalSymbol.name': globalSymbol.Name,
                'orcad.globalSymbol.symbolName': globalSymbol.SymbolName,
            },
            multiInstance: isMultiInstance ?
                {
                    instanceId: parent.designId,
                    displayModuleRef: globalSymbol.DbId.toString(),
                }
                : undefined,
        };
        if (globalSymbol.DisplayProperty) {
            const dp = globalSymbol.DisplayProperty;
            moduleProps.drawProperties[dp.Name] = {
                ...(0, ModelText_1.encodeStrippedText)({
                    raw: dp.Name === 'Name' ? globalSymbol.Name : moduleProps.name,
                    dialects: [ModelText_1.TextDialect.Orcad],
                }),
                pos: [dp.LocX + globalSymbol.LocX, dp.LocY + globalSymbol.LocY],
                layer: orcadLayers_1.OrCadSchLayerMap[sigma_engine_shared_1.SchLayerType.ValueLabel].id,
                angle: dp.Rotation * 90,
                justify: [-1, -1],
                size: Math.abs(dp.Font.Height / 2),
                thickness: 1,
                mirror: [false, false],
                hidden: false,
            };
        }
        let model;
        if (isGroundSymbol(globalSymbolDef)) {
            model = {
                modelId: 'ground.standard',
            };
        }
        else {
            model = {
                modelId: 'supply.standard',
            };
            try {
                const value = (0, sigma_engine_shared_1.parseNumericalSIValue)(globalSymbol.Name, sigma_engine_shared_1.BoundaryCharacteristicSIDefinitions['voltage']);
                model.inferenceReport = {
                    parameters: {
                        voltage: {
                            source: 'module-value',
                            value,
                        },
                    },
                };
            }
            catch {
            }
        }
        const project = TransformObject_1.TransformObject.FindParent(parent, TransformProject_1.TransformProject);
        const projectModule = new TransformProjectModule_1.TransformProjectModule(project, {
            name: moduleProps.name,
            component,
            model,
            value: globalSymbol.Name,
        });
        yield projectModule;
        const transformModule = new TransformModule_1.TransformModule(parent, moduleProps, component, projectModule);
        yield transformModule;
        if (isMultiInstance && instance.instanceIndex === 0) {
            const multiInstanceParent = parent.parent;
            if (multiInstanceParent instanceof TransformMultiInstanceSchematic_1.TransformMultiInstanceSchematic) {
                const displayModuleProps = {
                    ...moduleProps,
                    multiInstance: undefined,
                };
                yield new TransformMultiInstanceDisplayModule_1.TransformMultiInstanceDisplayModule(multiInstanceParent, displayModuleProps, component);
            }
        }
        if (pins.length === 0) {
            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                type: 'orcad-dsn-global-symbol-no-pins',
                message: `Global symbol "${globalSymbol.Name}" has no pins. No connections will be created.`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
            return;
        }
        if (pins.length > 1) {
            yield new TransformProcessingResult_1.TransformProcessingResult(parent, {
                type: 'orcad-dsn-global-symbol-multiple-pins',
                message: `Global symbol "${globalSymbol.Name}" has ${pins.length} pins. Only the first pin will be connected.`,
                severity: sigma_engine_shared_1.BoundaryLevel.Warning,
            });
        }
        const pin = globalSymbolDef.PinScalar?.[0];
        if (pin) {
            const pinPosition = OrcadDsnUtils_1.OrcadDsnUtils.transformPinToSchematicCoords({ HotptX: pin.HotptX, HotptY: pin.HotptY }, globalSymbol, globalSymbolDef.BoundingBox);
            yield new TransformModulePinConnection_1.TransformModulePinConnection(parent, {
                position: pinPosition,
                connection: {
                    moduleRef,
                    moduleName: moduleProps.name,
                    pinRef: pins[0].ref,
                    appliesNetName: {
                        label: globalSymbol.Name,
                        labelType: 'global',
                        strategyMetadata: {
                            'net-properties': (globalSymbol.UserProperty ?? []).map((property) => ({
                                name: property.Name,
                                value: property.Value,
                            })),
                        },
                    },
                },
            });
        }
    }
    findGlobalSymbol(symbols, globalSymbol) {
        if (!symbols) {
            return { symbol: undefined, ambiguous: false };
        }
        return (0, OrcadDsnSymbolUtils_1.resolveSymbolVariant)(symbols, globalSymbol.SymbolName, globalSymbol.LibName);
    }
}
exports.OrcadDsnTransformGlobalSymbolGenerator = OrcadDsnTransformGlobalSymbolGenerator;
//# sourceMappingURL=OrcadDsnTransformGlobalSymbolGenerator.js.map